import {
    Badge,
    Box,
    Button,
    HStack,
    Spinner,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import { EventDto } from '../../../models/event/dto/event.dto';
import { EventWaitlistEntryDto } from '../../../models/event/dto/event-waitlist-entry.dto';
import { READ_FAILED_I18N_KEY, classifyRead } from '../../_shared/readState';
import { useGetEventWaitlist } from '../hooks/useGetEventWaitlist';
import { usePromoteFromWaitlist } from '../hooks/usePromoteFromWaitlist';
import { useRemoveFromWaitlist } from '../hooks/useRemoveFromWaitlist';
import { splitWaitlistQueues } from '../utils/waitlist-queues';

const fmt = (iso: string) => new Date(iso).toLocaleString();

/**
 * Club-facing view of an event's waitlist. It holds two queues that mean
 * different things and are therefore rendered as two tables:
 *
 * - **Approval requests** (`reason: 'level'`, MYS-62) — out-of-level players
 *   the club approves or rejects. A *decision*.
 * - **Waiting for a seat** (`reason: 'capacity'`) — in-level players queued
 *   behind a full event. A *line*, and the club's action is to hand the next
 *   one a freed seat.
 *
 * Both use the same two endpoints: `promote` and `remove` are reason-agnostic
 * server-side (they look an entry up by event + user), so approving a capacity
 * entry enrols them on a free event and opens a payment window on a paid one,
 * exactly as it does for an approval request.
 *
 * On a paid event, promotion never enrols for free — it holds the slot until
 * the payment window lapses, which is why an approved entry in *either* table
 * shows "awaiting payment" rather than disappearing.
 */
const EventWaitlistCard: React.FC<{ event: EventDto }> = ({ event }) => {
    // R127-3 — the empty state below is a claim about players, and it used to be
    // made on a refused read as readily as on an answered one.
    //
    // `{ data: entries, isLoading }` took no error flag, so a 403/5xx on
    // `GET events/:id/waitlist` left `entries` undefined,
    // `splitWaitlistQueues(undefined)` returned two empty queues, and the card
    // told the club "no entries" — nobody is waiting for a seat and nobody is
    // waiting on an approval decision. Both are actionable and both were wrong:
    // an out-of-level player sits unapproved and a freed seat goes to nobody,
    // with no error anywhere to say why. This is D57's shape on the surface
    // where the cost lands on a member rather than on an operator.
    //
    // The raw `isLoading` was the second half. React Query v4 reports a DISABLED
    // query as `isLoading: true`, so an event id that never arrives spun this
    // card forever; `classifyRead` reads `isInitialLoading` and calls that state
    // what it is. `never-issued` renders the same notice as `unreadable` rather
    // than a prompt — an entity-gated read has no scope for the operator to
    // choose (R98-1) — and both fail CLOSED, saying nothing about who is queued.
    // R96-3 is preserved: a failed background refetch over a queue already on
    // screen keeps the queue, because `unreadable` requires `!data`.
    const {
        data: entries,
        isError: isWaitlistError,
        isInitialLoading,
    } = useGetEventWaitlist(event.id);
    const readState = classifyRead(isWaitlistError, isInitialLoading, entries);
    const promote = usePromoteFromWaitlist();
    const remove = useRemoveFromWaitlist();

    if (readState === 'loading') {
        return (
            <Box display="flex" justifyContent="center" p={6}>
                <Spinner />
            </Box>
        );
    }

    if (readState !== 'answered') {
        return (
            <Box p={4}>
                <InlineErrorState message={translate(READ_FAILED_I18N_KEY)} />
            </Box>
        );
    }

    const { approvalRequests, capacityQueue } = splitWaitlistQueues(entries);

    if (approvalRequests.length === 0 && capacityQueue.length === 0) {
        return (
            <Box p={4}>
                <Text color="gray.500">{translate('event.waitlist.no_entries')}</Text>
            </Box>
        );
    }

    const eventRange =
        event.minLevel && event.maxLevel
            ? `${event.minLevel} - ${event.maxLevel}`
            : event.minLevel
                ? `${event.minLevel}+`
                : event.maxLevel
                    ? `<= ${event.maxLevel}`
                    : '-';

    const renderStatus = (entry: EventWaitlistEntryDto) => {
        if (entry.status === 'approved') {
            const deadline = entry.reservedUntil
                ? ` (${translate('event.waitlist.pay_by')} ${fmt(entry.reservedUntil)})`
                : '';
            return (
                <Badge colorScheme="orange">
                    {`${translate('event.waitlist.awaiting_payment')}${deadline}`}
                </Badge>
            );
        }
        // A capacity entry is not awaiting a decision — it is waiting for a
        // seat. Showing it as "pending approval" would tell an admin there is
        // something to approve when there is nothing to approve until a spot
        // frees.
        if (entry.reason !== 'level') {
            return (
                <Badge colorScheme="blue">
                    {translate('event.waitlist.waiting_for_seat')}
                </Badge>
            );
        }
        return (
            <Badge colorScheme="yellow">{translate('event.waitlist.pending')}</Badge>
        );
    };

    /**
     * Shared so the two tables cannot drift into offering different actions on
     * the same server endpoints. Only the labels differ: "approve/reject" is a
     * verdict on a request, "promote/remove" is a movement in a line.
     */
    const renderActions = (
        entry: EventWaitlistEntryDto,
        promoteLabel: string,
        removeLabel: string,
    ) => (
        <HStack spacing={2}>
            <Button
                size="xs"
                colorScheme="green"
                // An approved hold cannot be approved again — the server
                // rejects it with "already been approved and is awaiting
                // payment".
                isDisabled={entry.status !== 'pending'}
                isLoading={
                    promote.isLoading && promote.variables?.userId === entry.user.id
                }
                onClick={() =>
                    promote.mutate({
                        eventId: event.id,
                        userId: entry.user.id,
                    })
                }
            >
                {promoteLabel}
            </Button>
            <Button
                size="xs"
                colorScheme="red"
                variant="outline"
                isLoading={
                    remove.isLoading && remove.variables?.userId === entry.user.id
                }
                onClick={() =>
                    remove.mutate({
                        eventId: event.id,
                        userId: entry.user.id,
                    })
                }
            >
                {removeLabel}
            </Button>
        </HStack>
    );

    return (
        <VStack align="stretch" spacing={6} p={4}>
            {approvalRequests.length > 0 && (
                <VStack align="stretch" spacing={3}>
                    <Text fontWeight="semibold">
                        {`${translate('event.waitlist.approval_requests')} (${approvalRequests.length})`}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        {`${translate('event.waitlist.event_level_range')}: ${eventRange}`}
                    </Text>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th>{translate('event.waitlist.player')}</Th>
                                <Th isNumeric>{translate('event.waitlist.level')}</Th>
                                <Th>{translate('event.waitlist.requested_at')}</Th>
                                <Th>{translate('event.waitlist.status')}</Th>
                                <Th>{translate('event.waitlist.actions')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {approvalRequests.map((entry) => (
                                <Tr key={entry.id}>
                                    <Td>
                                        {entry.user.firstName} {entry.user.lastName}
                                    </Td>
                                    <Td isNumeric>{entry.user.level ?? '-'}</Td>
                                    <Td>{fmt(entry.createdAt)}</Td>
                                    <Td>{renderStatus(entry)}</Td>
                                    <Td>
                                        {renderActions(
                                            entry,
                                            translate('event.waitlist.approve'),
                                            translate('event.waitlist.reject'),
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </VStack>
            )}

            {capacityQueue.length > 0 && (
                <VStack align="stretch" spacing={3}>
                    <Text fontWeight="semibold">
                        {`${translate('event.waitlist.seat_queue')} (${capacityQueue.length})`}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                        {translate('event.waitlist.seat_queue_hint')}
                    </Text>
                    <Table size="sm" variant="simple">
                        <Thead>
                            <Tr>
                                <Th isNumeric>{translate('event.waitlist.rank')}</Th>
                                <Th>{translate('event.waitlist.player')}</Th>
                                <Th>{translate('event.waitlist.requested_at')}</Th>
                                <Th>{translate('event.waitlist.status')}</Th>
                                <Th>{translate('event.waitlist.actions')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {capacityQueue.map((entry, index) => (
                                <Tr key={entry.id}>
                                    {/*
                                      * The rank is the index in THIS queue, not
                                      * `entry.position`. Positions are one
                                      * sequence shared with the approval queue
                                      * (H30), so a player who is first in line
                                      * for a seat can carry position 5. The
                                      * index matches the order
                                      * `notifyWaitlistOnSpotOpen` actually
                                      * notifies in, which is what makes this
                                      * number true.
                                      */}
                                    <Td isNumeric>{index + 1}</Td>
                                    <Td>
                                        {entry.user.firstName} {entry.user.lastName}
                                    </Td>
                                    <Td>{fmt(entry.createdAt)}</Td>
                                    <Td>{renderStatus(entry)}</Td>
                                    <Td>
                                        {renderActions(
                                            entry,
                                            translate('event.waitlist.promote'),
                                            translate('event.waitlist.remove'),
                                        )}
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </VStack>
            )}
        </VStack>
    );
};

export default EventWaitlistCard;
